import React from 'react';
import {
    Avatar, Badge, Box, IconButton, List, ListItemAvatar, ListItemButton, ListItemText,
    Stack, Typography, Tooltip,
} from '@mui/material';
import AddCommentIcon from '@mui/icons-material/AddComment';
import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import moment from 'moment';
import { useChat } from 'contexts/ChatContext';

function roomDisplayName(room, myId) {
    if (room.type === 'global') return room.name || '🌐 Global';
    if (room.type === 'aibot') return room.name || '🤖 Chat bot';
    if (room.type === 'group') return room.name || 'Group';
    const other = (room.participants || []).find((p) => (p._id || p) !== myId);
    return other ? other.fullname || other.username : room.name || 'Chat';
}

// Avatar icon + color per room type.
function roomAvatar(room) {
    if (room.type === 'global') return { Icon: PublicIcon, bg: 'success.main' };
    if (room.type === 'aibot') return { Icon: SmartToyIcon, bg: '#7c3aed' };
    if (room.type === 'group') return { Icon: GroupIcon, bg: 'primary.main' };
    return { Icon: PersonIcon, bg: 'secondary.main' };
}

export default function RoomList({ onOpenRoom, onNewGroup, onClose }) {
    const { rooms, unreadByRoom, myId } = useChat();

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h4">Chat</Typography>
                <Stack direction="row" spacing={0.5}>
                    <Tooltip title="New group">
                        <IconButton color="primary" onClick={onNewGroup}><AddCommentIcon /></IconButton>
                    </Tooltip>
                    {onClose && (
                        <Tooltip title="Close">
                            <IconButton onClick={onClose}><CloseIcon /></IconButton>
                        </Tooltip>
                    )}
                </Stack>
            </Stack>

            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {rooms.length === 0 && (
                    <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">ຍັງບໍ່ມີຫ້ອງສົນທະນາ</Typography>
                        <Typography variant="caption">กดปุ่ม + เพื่อสร้างกลุ่มใหม่</Typography>
                    </Box>
                )}
                <List disablePadding>
                    {rooms.map((room) => {
                        const unread = unreadByRoom[room._id] || 0;
                        const name = roomDisplayName(room, myId);
                        const { Icon: AvatarIcon, bg } = roomAvatar(room);
                        return (
                            <ListItemButton key={room._id} onClick={() => onOpenRoom(room)} divider>
                                <ListItemAvatar>
                                    <Badge color="error" badgeContent={unread} max={99}>
                                        <Avatar sx={{ bgcolor: bg }}>
                                            <AvatarIcon />
                                        </Avatar>
                                    </Badge>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="subtitle1" noWrap sx={{ fontWeight: unread ? 700 : 500, maxWidth: 180 }}>
                                                {name}
                                            </Typography>
                                            {room.lastMessageAt && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {moment(room.lastMessageAt).fromNow(true)}
                                                </Typography>
                                            )}
                                        </Stack>
                                    }
                                    secondary={
                                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 240 }}>
                                            {room.lastMessage || '—'}
                                        </Typography>
                                    }
                                />
                            </ListItemButton>
                        );
                    })}
                </List>
            </Box>
        </Box>
    );
}
